/** What the clause filters are: a dimension a clause holds values on, the selection, and
 * how a list is narrowed by it. The bar that sets them is ClauseFilters.tsx. */
import { useState } from "react";

import type { RegisterLine } from "../api";
import { type Grouping, groupOf } from "./clauseStatus";

export interface FilterDim<L> {
  key: string;
  noun: { one: string; many: string };
  /** The values a clause holds on this dimension. */
  values: (line: L) => string[];
  labelOf: (value: string) => string;
  /** The order options are offered in; otherwise by label. */
  order?: string[];
}

export interface FilterState {
  selected: Record<string, string[]>;
  search: string;
}

export const useClauseFilters = () =>
  useState<FilterState>({ selected: {}, search: "" });

export const filterClauses = <L extends RegisterLine>(
  lines: L[],
  dims: FilterDim<L>[],
  state: FilterState,
): L[] => {
  const needle = state.search.trim().toLowerCase();
  return lines.filter(
    (line) =>
      dims.every((dim) => {
        const chosen = state.selected[dim.key] ?? [];
        return chosen.length === 0 || dim.values(line).some((v) => chosen.includes(v));
      }) &&
      (!needle ||
        `${line.reference} ${line.title} ${line.text}`.toLowerCase().includes(needle)),
  );
};

/** The goal or group filter: each clause under the goal or group it is shown in. */
export const groupDim = <L extends RegisterLine>(grouping: Grouping): FilterDim<L> => {
  const goalTitle = new Map(grouping.goals.map((g) => [g.key, `Goal: ${g.title}`]));
  return {
    key: "group",
    noun: { one: "goal or group", many: "goals and groups" },
    values: (line) => [groupOf(line, grouping)],
    labelOf: (v) => goalTitle.get(v) ?? grouping.titleOf(v),
    order: [...grouping.goals.map((g) => g.key), ...grouping.order],
  };
};
